/**
 * CTA Image Parallax Component
 *
 * Creates a parallax effect on call-to-action images as they scroll into view:
 * - Moves image vertically (yPercent) for depth effect
 *
 * Uses GSAP and ScrollTrigger for smooth animations.
 * Works seamlessly with Lenis smooth scroll.
 */

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize CTA image parallax effect
 * Finds all elements with [data-cta-image-parallax] and applies the effect
 *
 * Performance optimized for mobile devices:
 * - Reduced parallax intensity on mobile
 * - Faster scrub value for smoother tracking
 * - GPU acceleration with force3D
 */
export function initCtaImageParallax() {
  const parallaxElements = document.querySelectorAll(
    "[data-cta-image-parallax]",
  );

  if (parallaxElements.length === 0) {
    return; // No parallax elements found
  }

  // Detect mobile devices
  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  // Adjust parallax settings based on device
  const parallaxAmount = isMobile ? 0 : 30; // Positive value moves image down (opposite of footer)
  const scrubValue = isMobile ? 0 : 0.8; // true = 0.5s (smooth), 0.5 = faster for mobile

  parallaxElements.forEach((el) => {
    const inner = el.querySelector("[data-cta-image-parallax-inner]");

    if (!inner) {
      return; // Skip if inner element not found
    }

    // Create timeline with scroll trigger
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top bottom", // When top of element hits bottom of viewport
        end: "bottom top", // When bottom of element hits top of viewport
        scrub: scrubValue, // Smooth scrubbing
        // markers: true, // Uncomment for debugging
      },
    });

    // Animate image inner content (parallax movement)
    tl.from(inner, {
      yPercent: parallaxAmount,
      ease: "linear",
      force3D: true, // Enable GPU acceleration
    });
  });
}

/**
 * Cleanup function to destroy ScrollTrigger instances
 * Useful for SPA navigation or dynamic content updates
 */
export function destroyCtaImageParallax() {
  ScrollTrigger.getAll().forEach((trigger) => {
    if (trigger.vars.trigger?.hasAttribute("data-cta-image-parallax")) {
      trigger.kill();
    }
  });
}

export const ctaImageParallax = {
  init: initCtaImageParallax,
  destroy: destroyCtaImageParallax,
};
